import {
  AdvancedFilterModel,
  BooleanAdvancedFilterModelType,
  FilterModel,
  ScalarAdvancedFilterModelType,
  TextAdvancedFilterModelType,
} from 'ag-grid-enterprise';

export function doesRowMatchFilterModel(row: any, filterModel: FilterModel | null | undefined): boolean {
  if (!filterModel) {
    return true;
  }
  for (const [colId, model] of Object.entries(filterModel)) {
    if (!model) {
      continue;
    }
    const value = row?.[colId];
    if (!validateDataAgainstModel(value, model as AdvancedFilterModel)) {
      return false;
    }
  }
  return true;
}

export function validateDataAgainstModel(data: any, model: AdvancedFilterModel): boolean {
  if (model.filterType === 'join') {
    if (!Array.isArray(model.conditions)) {
      return false;
    }
    if (model.type === 'AND') {
      return model.conditions.every(condition => validateDataAgainstModel(data, condition));
    }
    if (model.type === 'OR') {
      return model.conditions.some(condition => validateDataAgainstModel(data, condition));
    }
    return false;
  }
  switch (model.filterType) {
    case 'text':
      return validateTextCondition(data, model.type, model.filter);
    case 'number':
      return validateScalarCondition(data, model.type, model.filter);
    case 'date':
    case 'dateString':
      return validateScalarCondition(
        data ? new Date(data).getTime() : undefined,
        model.type,
        model.filter ? new Date(model.filter).getTime() : undefined
      );
    case 'boolean':
      return validateBooleanCondition(data, model.type);
    default:
      return false;
  }
}

export function removeFilterCondition(
  model: AdvancedFilterModel | null | undefined,
  condition: AdvancedFilterModel | null | undefined
): AdvancedFilterModel | null {
  if (!model || !condition) {
    return null;
  }
  if (model.filterType !== 'join') {
    return JSON.stringify(model) === JSON.stringify(condition) ? null : model;
  }
  const conditions = model.conditions
    .map(current => removeFilterCondition(current, condition))
    .filter((current): current is AdvancedFilterModel => !!current);

  if (conditions.length === 0) {
    return null;
  }
  if (conditions.length === 1) {
    return conditions[0];
  }
  return { ...model, conditions };
}

function validateTextCondition(data: unknown, type: TextAdvancedFilterModelType, filter?: string): boolean {
  const value = data == null ? '' : String(data);
  switch (type) {
    case 'equals':
      return value === filter;
    case 'notEqual':
      return value !== filter;
    case 'contains':
      return filter ? value.includes(filter) : false;
    case 'notContains':
      return filter ? !value.includes(filter) : true;
    case 'startsWith':
      return filter ? value.startsWith(filter) : false;
    case 'endsWith':
      return filter ? value.endsWith(filter) : false;
    case 'blank':
      return value === '';
    case 'notBlank':
      return value !== '';
    default:
      return false;
  }
}

function validateScalarCondition(
  data: number | undefined,
  type: ScalarAdvancedFilterModelType,
  filter?: number
): boolean {
  switch (type) {
    case 'equals':
      return data === filter;
    case 'notEqual':
      return data !== filter;
    case 'lessThan':
      return filter !== undefined && data !== undefined && data < filter;
    case 'lessThanOrEqual':
      return filter !== undefined && data !== undefined && data <= filter;
    case 'greaterThan':
      return filter !== undefined && data !== undefined && data > filter;
    case 'greaterThanOrEqual':
      return filter !== undefined && data !== undefined && data >= filter;
    case 'blank':
      return data === null || data === undefined;
    case 'notBlank':
      return data !== null && data !== undefined;
    default:
      return false;
  }
}

function validateBooleanCondition(data: unknown, type: BooleanAdvancedFilterModelType): boolean {
  return (type === 'true' && data === true) || (type === 'false' && data === false);
}
